import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    ChangeEvent,
    FormEvent,
} from 'react';

import { Link, router } from '@inertiajs/react';
import debounce from 'lodash/debounce';
import { Loader2, Search } from 'lucide-react';

import { ScalePreview } from './ScalePreview.tsx';
import { Routes } from '../../models/routes';
import { CartonProduct } from '../../types';
import { getProductsCountText } from '../../utils';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Image } from '../ui/Image';
import { Input } from '../ui/Input';

const SearchBar = forwardRef<{ focus: () => void }>((_, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<CartonProduct[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const abortControllerRef = useRef<AbortController | null>(null);

    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
            setIsOpen(true);
        },
    }));

    const fetchProducts = useCallback(async (query: string) => {
        abortControllerRef.current?.abort();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const signal = abortController.signal;

        try {
            const res = await fetch(
                `${Routes.ActiveSearch}?q=${encodeURIComponent(query)}`,
                { signal },
            );
            const data = await res.json();

            if (!signal.aborted) {
                setSearchResults(data ?? []);
                setIsLoading(false);
            }
        } catch (err) {
            if (!signal.aborted) {
                console.error('Fetch error:', err);
                setSearchResults([]);
                setIsLoading(false);
            }
        }
    }, []);

    const debouncedFetch = useMemo(
        () =>
            debounce((val: string) => {
                void fetchProducts(val);
            }, 300),
        [fetchProducts],
    );

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
            abortControllerRef.current?.abort();
        };
    }, [debouncedFetch]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setSearchQuery(value);
        setSearchResults([]);

        if (value && value.length > 2) {
            setIsLoading(true);
            debouncedFetch(value);
        } else {
            debouncedFetch.cancel();
            abortControllerRef.current?.abort();
            setSearchResults([]);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleResultClick = (product: CartonProduct) => {
        setIsOpen(false);
        if (product.url_path) {
            router.visit(product.url_path);
        }
    };

    const searchPageUrl = `${Routes.Search}?q=${encodeURIComponent(
        searchQuery.trim(),
    )}`;

    const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!searchQuery.trim()) return;

        setIsOpen(false);
        router.visit(searchPageUrl);
    };

    useEffect(() => {
        if (isOpen && searchQuery.length > 2) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, searchQuery]);

    const resultsFound = searchResults.length > 0;
    const visibleSearchResults = searchResults.slice(0, 8);

    return (
        <div className="relative block">
            <form onSubmit={handleSearchSubmit}>
                <div className="relative">
                    <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        ref={inputRef}
                        type="search"
                        placeholder="Пошук"
                        className="h-10 w-full pl-9 text-base md:h-9 md:w-[120px] md:pl-8 md:text-xs lg:w-[250px] xl:w-[350px]"
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={() => setIsOpen(true)}
                    />
                </div>
            </form>

            {isOpen && searchQuery.length > 2 && (
                <div className="fixed top-30 left-0 z-[70] h-dvh w-full bg-black/60 md:top-16.5">
                    <div
                        ref={containerRef}
                        className="fixed top-30 right-0 left-0 z-[80] max-h-96 overflow-y-auto rounded-b-md border bg-background shadow-lg md:top-16"
                    >
                        <div
                            className="container"
                            data-testid="global-search-results"
                        >
                            {!isLoading && resultsFound && (
                                <div className="py-2 text-xs font-medium text-muted-foreground">
                                    Знайдено{' '}
                                    {getProductsCountText(searchResults.length)}
                                </div>
                            )}

                            {!isLoading &&
                                visibleSearchResults.map((product) => {
                                    return (
                                        <button
                                            key={product.id}
                                            data-testid={`global-search-result-${product.id}`}
                                            className="flex w-full items-center gap-3 border-b border-border/50 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50"
                                            onClick={() =>
                                                handleResultClick(product)
                                            }
                                        >
                                            <ScalePreview
                                                triggerClassName="h-8 w-6 bg-muted"
                                                side="right"
                                            >
                                                <Image
                                                    src={product.image}
                                                    alt={product.image_alt}
                                                    className="object-cover"
                                                />
                                            </ScalePreview>

                                            <Link href={product.url_path}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <h4 className="truncate text-sm font-medium">
                                                                {product.name}
                                                            </h4>
                                                        </div>
                                                    </div>

                                                    {/* Badges */}
                                                    <div className="mt-2 flex items-center gap-1">
                                                        {product.universe_name && (
                                                            <Badge
                                                                variant="neutral"
                                                                className="text-xs"
                                                            >
                                                                {
                                                                    product.universe_name
                                                                }
                                                            </Badge>
                                                        )}
                                                        <Badge
                                                            variant="default"
                                                            className="text-xs"
                                                        >
                                                            {product.language ||
                                                                'N/A'}
                                                        </Badge>
                                                        <Badge
                                                            variant="outline"
                                                            className="text-xs"
                                                        >
                                                            {product.expansion_tag ||
                                                                'N/A'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </Link>
                                        </button>
                                    );
                                })}

                            {!isLoading && resultsFound && (
                                <div className="py-3 text-center">
                                    <Button
                                        variant="outline"
                                        className="rounded-xl"
                                        asChild
                                    >
                                        <Link
                                            href={searchPageUrl}
                                            data-testid="global-search-all-results"
                                            onClick={() => setIsOpen(false)}
                                        >
                                            Переглянути всі результати
                                        </Link>
                                    </Button>
                                </div>
                            )}

                            {searchQuery.length > 2 && !resultsFound && (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                    {isLoading ? (
                                        <Loader2 className="m-auto h-4 w-4 animate-spin" />
                                    ) : (
                                        `Товари не знайдено для "${searchQuery}"`
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
